import { CLIENT_CAPABILITIES } from './clientCapabilities';

// Helper function to check if user has permission
export const hasPermission = (
  userPermissions: string[] | null | undefined,
  requiredPermission: string,
): boolean => {
  return (userPermissions ?? []).includes(requiredPermission);
};

// Helper function to check if user has any of the specified permissions
export const hasAnyPermission = (
  userPermissions: string[] | null | undefined,
  requiredPermissions: string[],
): boolean => {
  if (!userPermissions) return false;
  return requiredPermissions.some((permission) =>
    userPermissions.includes(permission)
  );
};

/**
 * Checks whether a user can manage technician assignments on a job.
 * Allows Platform/Super Admins as well as Trade/Company Owners, Admins, and Managers
 * whose company matches the assigned trade company on the job.
 */
export function canManageJobAssignments(
  role: string | null | undefined,
  companyId: string | null | undefined,
  jobTradeId: string | null | undefined
): boolean {
  const r = (role ?? '').toUpperCase();
  const isPlatform = ['PLATFORM_ADMIN', 'SUPER_ADMIN'].includes(r);
  const isCompanyLead = [
    'TRADE_OWNER',
    'TRADE_ADMIN',
    'TRADE_MANAGER',
    'COMPANY_OWNER',
    'COMPANY_ADMIN',
    'OWNER',
    'ADMIN',
    'MANAGER',
  ].includes(r);
  return isPlatform || (isCompanyLead && !!companyId && companyId === jobTradeId);
}

export interface CompanyRolePermissionOverrides {
  tech_can_view_financials?: boolean;
  tech_can_accept_jobs?: boolean;
  apprentice_can_view_financials?: boolean;
  apprentice_can_accept_jobs?: boolean;
}

export interface UserDelegationState {
  is_delegated_acceptor?: boolean | null;
  delegation_expires_at?: string | null;
}

/**
 * Roles as the various call sites actually hold them.
 *
 * sync_user_session returns objects ({ role_name }), while user_management_view
 * and the team hooks return plain strings. Accepting both stops a caller from
 * silently failing every role check by passing the wrong shape.
 */
export type RoleList =
  | Array<{ role_name: string } | string>
  | null
  | undefined;

function toRoleNames(roles: RoleList): string[] {
  if (!Array.isArray(roles)) return [];
  return roles
    .map((r) => (typeof r === 'string' ? r : r?.role_name))
    .filter((name): name is string => typeof name === 'string' && name.length > 0);
}

/**
 * Whether an on-call job acceptance delegation is currently in force.
 *
 * Nothing clears `is_delegated_acceptor` when the expiry passes — expiry is
 * evaluated at read time — so every consumer (badges, banners, status columns,
 * the acceptance check itself) has to go through this helper or it will report
 * a lapsed delegation as active.
 */
export function isDelegationActive(
  delegation?: UserDelegationState | null,
  now: number = Date.now(),
): boolean {
  if (!delegation?.is_delegated_acceptor) return false;
  if (!delegation.delegation_expires_at) return true;
  const expires = new Date(delegation.delegation_expires_at).getTime();
  return !Number.isNaN(expires) && expires > now;
}

/**
 * Checks whether a user has permission to view financial metrics (pricing, margins, trade payouts).
 * By default, Owners, Admins, and Managers have full visibility.
 * Technicians, Apprentices, and other field roles are hidden by default unless explicitly permitted
 * or configured via company role settings overrides.
 */
export function canViewFinancials(
  role: string | null | undefined,
  permissions?: string[] | null,
  companySettings?: CompanyRolePermissionOverrides | null,
): boolean {
  const r = (role ?? '').toUpperCase();
  if ([
    'SUPER_ADMIN',
    'PLATFORM_ADMIN',
    'PLATFORM_MANAGER',
    'PLATFORM',
    'TRADE_OWNER',
    'TRADE_ADMIN',
    'TRADE_MANAGER',
    'TRADE_COMPANY',
    'TRADE',
    'CUSTOMER_OWNER',
    'CUSTOMER_ADMIN',
    // Not 'CUSTOMER' / 'CUSTOMER_COMPANY' (an account type, which every client user has): a
    // Coordinator must not see financials through it (client app PR1, G24).
    'OWNER',
    'ADMIN',
    'MANAGER',
  ].includes(r)) {
    return true;
  }
  if (hasPermission(permissions, 'financial:view')) {
    return true;
  }
  if ((r === 'TECHNICIAN' || r === 'TRADE_USER') && companySettings?.tech_can_view_financials) {
    return true;
  }
  if ((r === 'APPRENTICE' || r === 'TA') && companySettings?.apprentice_can_view_financials) {
    return true;
  }
  return false;
}

/**
 * Checks whether a user has permission to accept job offers on behalf of the company.
 * Restricted to Owners, Admins, and Managers by default, or technicians with role overrides,
 * or technicians with an active, non-expired on-call / boss-away delegation.
 */
export function canAcceptJob(
  role: string | null | undefined,
  permissions?: string[] | null,
  companySettings?: CompanyRolePermissionOverrides | null,
  delegation?: UserDelegationState | null,
): boolean {
  const r = (role ?? '').toUpperCase();
  if ([
    'SUPER_ADMIN',
    'PLATFORM_ADMIN',
    'PLATFORM_MANAGER',
    'TRADE_OWNER',
    'TRADE_ADMIN',
    'TRADE_MANAGER',
    'OWNER',
    'ADMIN',
    'MANAGER',
  ].includes(r)) {
    return true;
  }
  if (hasPermission(permissions, 'job:accept')) {
    return true;
  }
  if ((r === 'TECHNICIAN' || r === 'TRADE_USER') && companySettings?.tech_can_accept_jobs) {
    return true;
  }
  if ((r === 'APPRENTICE' || r === 'TA') && companySettings?.apprentice_can_accept_jobs) {
    return true;
  }
  // Individual on-call / boss-away delegation
  if (isDelegationActive(delegation)) {
    return true;
  }
  return false;
}

/**
 * Whether a user may GRANT or REVOKE on-call job acceptance delegation.
 *
 * Deliberately narrower than canAcceptJob: managers can accept jobs themselves
 * but cannot delegate that authority to others. Must stay in step with the role
 * list in public.toggle_staff_job_delegation / public.revoke_all_job_delegations
 * — showing the action to someone the RPC will reject just yields an error toast.
 */
export function canDelegateJobAcceptance(
  roles: RoleList,
  isPlatformAdmin = false,
): boolean {
  if (isPlatformAdmin) return true;
  return toRoleNames(roles).some((name) =>
    ['TRADE_OWNER', 'TRADE_ADMIN', 'OWNER', 'ADMIN'].includes(name.toUpperCase()),
  );
}

/**
 * Multi-role aware wrapper around canAcceptJob.
 *
 * Users can hold several roles (e.g. TECHNICIAN *and* TRADE_OWNER); evaluating
 * only `roles[0]` disables acceptance for an actual owner.
 */
export function canAnyRoleAcceptJob(
  roles: RoleList,
  permissions?: string[] | null,
  companySettings?: CompanyRolePermissionOverrides | null,
  delegation?: UserDelegationState | null,
): boolean {
  const names = toRoleNames(roles);
  if (names.length > 0) {
    return names.some((name) => canAcceptJob(name, permissions, companySettings, delegation));
  }
  return canAcceptJob(null, permissions, companySettings, delegation);
}

/**
 * Checks whether a user can view trade payout transactions and download Remittance Advices on a job.
 * Strictly limited to Platform Admins, or members of the assigned trade company with financial permissions.
 */
export function canViewTradePayouts(
  role: string | null | undefined,
  permissions: string[] | null | undefined,
  companySettings: CompanyRolePermissionOverrides | null | undefined,
  userTradeId: string | null | undefined,
  jobTradeId: string | null | undefined,
  isPlatformAdmin?: boolean
): boolean {
  if (isPlatformAdmin) return true;
  const r = (role ?? '').toUpperCase();
  if (['SUPER_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_MANAGER'].includes(r)) return true;

  if (!userTradeId || !jobTradeId || userTradeId !== jobTradeId) {
    return false;
  }

  return canViewFinancials(role, permissions, companySettings);
}

/**
 * Checks whether a user can view client tax invoices and billing receipts on a job.
 * Platform Admins, or members of the customer company holding client:financial_docs (client app
 * PR1, F6 B: Owners and Admins; never a Coordinator). The role argument only serves the platform
 * branch: client access follows the capability key in the session.
 */
export function canViewClientInvoices(
  role: string | null | undefined,
  permissions: string[] | null | undefined,
  userCustomerId: string | null | undefined,
  jobCustomerId: string | null | undefined,
  isPlatformAdmin?: boolean
): boolean {
  if (isPlatformAdmin) return true;
  const r = (role ?? '').toUpperCase();
  if (['SUPER_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_MANAGER'].includes(r)) return true;

  if (!userCustomerId || !jobCustomerId || userCustomerId !== jobCustomerId) {
    return false;
  }

  return hasPermission(permissions, CLIENT_CAPABILITIES.financialDocs);
}

/**
 * Checks whether a user can view platform fee invoices (e.g. late cancellation fee, site access fee).
 */
export function canViewPlatformFees(
  role: string | null | undefined,
  permissions: string[] | null | undefined,
  userCustomerId: string | null | undefined,
  jobCustomerId: string | null | undefined,
  isPlatformAdmin?: boolean
): boolean {
  return canViewClientInvoices(role, permissions, userCustomerId, jobCustomerId, isPlatformAdmin);
}

/**
 * Checks whether a user can view platform commission and profit margin details.
 * Strictly restricted to platform administration staff.
 */
export function canViewPlatformMargins(
  role: string | null | undefined,
  isPlatformAdmin?: boolean
): boolean {
  if (isPlatformAdmin) return true;
  const r = (role ?? '').toUpperCase();
  return ['SUPER_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_MANAGER', 'PLATFORM_SUPPORT'].includes(r);
}

/**
 * Checks whether a user can view a specific supporting document on a job.
 */
export function canViewJobSupportingDocuments(
  isPublic: boolean | null | undefined,
  isAssignedTrade: boolean,
  isOwningClient: boolean,
  isPlatformAdmin: boolean
): boolean {
  if (isPublic) return true;
  return isPlatformAdmin || isAssignedTrade || isOwningClient;
}

/**
 * Roles that see their whole company's pending post-completion follow-ups,
 * rather than only the workflows assigned to them personally.
 */
export const COMPANY_FOLLOW_UP_VIEWER_ROLES = [
  'SUPER_ADMIN',
  'PLATFORM_ADMIN',
  'PLATFORM_MANAGER',
  'TRADE_OWNER',
  'TRADE_ADMIN',
  'TRADE_MANAGER',
  'COMPANY_OWNER',
  'COMPANY_ADMIN',
  'OWNER',
  'ADMIN',
  'MANAGER',
] as const;

/**
 * Whether this user should see company-wide pending follow-ups.
 *
 * Checks the primary role AND every secondary role: a company owner whose
 * primary role row is TECHNICIAN would otherwise be scoped down to their own
 * assigned workflows and lose sight of the company's outstanding compliance.
 *
 * Web and mobile must both go through this helper. They previously inlined
 * their own role lists, which disagreed (mobile omitted the unprefixed
 * OWNER/ADMIN/MANAGER aliases), so the same user was treated as a manager on
 * desktop and a technician on mobile.
 */
export function canViewCompanyFollowUps(
  role: string | null | undefined,
  roles?: RoleList,
  isPlatformAdmin?: boolean,
): boolean {
  if (isPlatformAdmin) return true;
  const names = [role, ...toRoleNames(roles)]
    .filter((n): n is string => typeof n === 'string' && n.length > 0)
    .map((n) => n.toUpperCase());
  return names.some((n) =>
    (COMPANY_FOLLOW_UP_VIEWER_ROLES as readonly string[]).includes(n),
  );
}

/**
 * Role names `public.is_admin_of_customer` accepts, kept in step with that SQL
 * function by hand. It is the check behind every client-side money decision
 * (`handle-admin-approval`, `release-milestone`, `respond-to-payout-release`,
 * `approve-variation`), so a frontend that gates on anything looser shows a
 * "Release Payout" button that fails with a 400 the moment it is pressed.
 */
export const CUSTOMER_ADMIN_ROLE_NAMES = [
  'OWNER',
  'ADMIN',
  'CLIENT ADMIN',
  'CUSTOMER_OWNER',
  'CUSTOMER_ADMIN',
] as const;

/**
 * Frontend mirror of `public.is_admin_of_customer(user_id, customer_id)`: the
 * user belongs to the job's customer AND holds an owner/admin role there. Plain
 * membership of the client company is not enough.
 *
 * Expired role assignments never reach this helper: loadUserProfile filters on
 * `expires_at` when it builds `user.roles`, as the SQL function does.
 */
export function isAdminOfCustomer(
  roles: RoleList,
  userCustomerId: string | null | undefined,
  jobCustomerId: string | null | undefined,
): boolean {
  if (!userCustomerId || !jobCustomerId || userCustomerId !== jobCustomerId) {
    return false;
  }
  return toRoleNames(roles).some((name) =>
    (CUSTOMER_ADMIN_ROLE_NAMES as readonly string[]).includes(name.toUpperCase()),
  );
}
